
import { useState, useEffect, ReactNode } from 'react';
import { AuthContext, AuthContextType } from './AuthContext';
import { AuthUser } from '@/types/auth';
import { toast } from '@/components/ui/use-toast';
import * as Sentry from "@sentry/react";
import { 
  loadStoredUser, 
  performLogin, 
  performSignup, 
  performLogout, 
  handleAuthStateChange 
} from '@/utils/auth';
import { supabase } from '@/integrations/supabase/client';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  console.log('AuthProvider - Initial render', { hasUser: !!user, isLoading });

  useEffect(() => {
    console.log("Setting up auth state listener");
    
    // Track authentication flow in Sentry
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'AuthProvider initialized',
      level: 'info',
    });
    
    // Set a timeout to prevent getting stuck in loading state
    const loadingTimeout = setTimeout(() => {
      if (isLoading) {
        console.log("Auth loading timeout reached - forcing loading state to false");
        Sentry.captureMessage("Auth loading timeout reached", "warning");
        setIsLoading(false);
      }
    }, 5000); // 5 second timeout
    
    // Set up auth state change listener FIRST - with non-async callback to prevent deadlocks
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("Auth state changed:", event, session?.user?.id);
        
        // Track auth state changes in Sentry
        Sentry.addBreadcrumb({
          category: 'auth',
          message: `Auth state changed: ${event}`,
          level: 'info',
          data: { event, userId: session?.user?.id }
        });
        
        if (session) {
          console.log("Session detected, will process user data");
          
          // Use setTimeout to defer async operations and prevent deadlocks
          setTimeout(async () => {
            try {
              console.log("Processing session data asynchronously");
              setIsLoading(true);
              const userData = await handleAuthStateChange(session);
              console.log("User data processed:", userData);
              if (userData) {
                setUser(userData);
                console.log("User state updated:", userData);
                
                // Set user in Sentry
                Sentry.setUser({
                  id: userData.id,
                  email: userData.email,
                  username: userData.email.split('@')[0],
                });
                
                // Add user roles to Sentry tags
                if (userData.roles && userData.roles.length > 0) {
                  Sentry.setTag('user_role', userData.roles.join(','));
                }
                
                // Add user demographics to Sentry context if available
                if (userData.ageRange || userData.educationLevel || userData.country) {
                  Sentry.setContext("user_demographics", {
                    ageRange: userData.ageRange,
                    educationLevel: userData.educationLevel,
                    country: userData.country,
                  });
                }
                
                if (event === 'SIGNED_IN') {
                  toast({
                    title: "Success",
                    description: "You have successfully logged in.",
                  });
                }
              } else {
                console.error("No user data returned from handleAuthStateChange");
                Sentry.captureMessage("No user data returned from handleAuthStateChange", "error");
              }
            } catch (error) {
              console.error("Error handling auth state change:", error);
              Sentry.captureException(error, {
                tags: { action: 'handle_auth_state_change' },
              });
            } finally {
              console.log("Finished processing auth state change");
              setIsLoading(false);
            }
          }, 0);
        } else if (event === 'SIGNED_OUT') {
          console.log("User signed out, clearing user state");
          setUser(null);
          localStorage.removeItem('authUser');
          
          // Clear user from Sentry
          Sentry.setUser(null);
        }
      }
    );

    // THEN check for existing session
    const checkSession = async () => {
      try {
        console.log("Checking for existing session");
        const { data: { session } } = await supabase.auth.getSession();
        console.log("Session check result:", { hasSession: !!session });
        if (session) {
          console.log("Existing session found, processing user data");
          const userData = await handleAuthStateChange(session);
          if (userData) {
            console.log("Setting user from existing session:", userData);
            setUser(userData);
            
            // Set user in Sentry
            Sentry.setUser({
              id: userData.id,
              email: userData.email,
              username: userData.email.split('@')[0],
            });
            
            // Add user roles to Sentry tags
            if (userData.roles && userData.roles.length > 0) {
              Sentry.setTag('user_role', userData.roles.join(','));
            }
          } else {
            console.error("No user data returned when processing existing session");
            Sentry.captureMessage("No user data returned when processing existing session", "error");
          }
        } else {
          console.log("No existing session found");
        }
      } catch (error) {
        console.error("Error getting session:", error);
        Sentry.captureException(error, {
          tags: { action: 'check_session' },
        });
      } finally {
        console.log("Session check complete, setting isLoading to false");
        setIsLoading(false);
      }
    };

    // Check if there's a stored user in localStorage
    const storedUser = loadStoredUser();
    if (storedUser) {
      console.log("Found stored user in localStorage:", storedUser);
      setUser(storedUser);
      
      // Set user in Sentry from stored user
      Sentry.setUser({
        id: storedUser.id,
        email: storedUser.email,
        username: storedUser.email.split('@')[0],
      });
      
      // Add user roles to Sentry tags
      if (storedUser.roles && storedUser.roles.length > 0) {
        Sentry.setTag('user_role', storedUser.roles.join(','));
      }
    } else {
      console.log("No stored user found in localStorage");
    }

    checkSession();

    return () => {
      console.log("Cleaning up auth state listener");
      subscription.unsubscribe();
      clearTimeout(loadingTimeout);
    };
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    setIsLoading(true);
    
    try {
      const loggedInUser = await performLogin(email, password);
      setUser(loggedInUser);
    } catch (error) {
      console.error('Login error:', error);
      toast({
        variant: "destructive",
        title: "Login Failed",
        description: error instanceof Error ? error.message : 'An unknown error occurred',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (
    email: string, 
    password: string, 
    ageRange: string, 
    educationLevel: string, 
    country?: string
  ): Promise<void> => {
    setIsLoading(true);
    
    try {
      const newUser = await performSignup(email, password, ageRange, educationLevel, country);
      setUser(newUser);
    } catch (error) {
      console.error('Signup error:', error);
      toast({
        variant: "destructive",
        title: "Signup Failed",
        description: error instanceof Error ? error.message : 'An unknown error occurred',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setIsLoading(true);
    
    try {
      await performLogout();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      toast({
        title: "Logged Out",
        description: "You have been logged out.",
      });
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    signup,
    logout,
  };

  console.log('AuthProvider - Providing context:', { 
    hasUser: !!user, 
    isAuthenticated: !!user, 
    isLoading,
    userRoles: user?.roles
  });

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
